import { cn } from '@/lib/cn'

const PALETTE = [
  'bg-[#0c66e4]',
  'bg-[#22a06b]',
  'bg-[#8f5bd3]',
  'bg-[#e34935]',
  'bg-[#d97008]',
  'bg-[#c93789]',
  'bg-[#0898a0]',
  'bg-[#42526e]',
  'bg-[#206a83]',
  'bg-[#7f5f01]',
  'bg-[#5e6c84]',
  'bg-[#6554c0]',
]

/**
 * Low-chroma counterpart to PALETTE, index-for-index, so a person keeps the same
 * hue identity in either tone. Use `soft` in dense lists, where a column of
 * saturated discs out-shouts the names beside them.
 */
const SOFT_PALETTE = [
  'bg-blue-100 text-blue-700 dark:bg-blue-400/15 dark:text-blue-300',
  'bg-emerald-100 text-emerald-700 dark:bg-emerald-400/15 dark:text-emerald-300',
  'bg-violet-100 text-violet-700 dark:bg-violet-400/15 dark:text-violet-300',
  'bg-red-100 text-red-700 dark:bg-red-400/15 dark:text-red-300',
  'bg-amber-100 text-amber-800 dark:bg-amber-400/15 dark:text-amber-300',
  'bg-pink-100 text-pink-700 dark:bg-pink-400/15 dark:text-pink-300',
  'bg-cyan-100 text-cyan-800 dark:bg-cyan-400/15 dark:text-cyan-300',
  'bg-slate-200 text-slate-700 dark:bg-slate-400/15 dark:text-slate-300',
  'bg-sky-100 text-sky-800 dark:bg-sky-400/15 dark:text-sky-300',
  'bg-yellow-100 text-yellow-800 dark:bg-yellow-400/15 dark:text-yellow-300',
  'bg-gray-200 text-gray-700 dark:bg-gray-400/15 dark:text-gray-300',
  'bg-indigo-100 text-indigo-700 dark:bg-indigo-400/15 dark:text-indigo-300',
]

const SIZES = {
  xs: 'h-5 w-5 text-[10px]',
  sm: 'h-6 w-6 text-[11px]',
  md: 'h-8 w-8 text-xs',
  lg: 'h-10 w-10 text-sm',
}

function hash(seed: string): number {
  let h = 0
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0
  return Math.abs(h)
}

/** A stable, subdued color for initials-only user avatars. */
export function getAvatarColorClass(seed: string): string {
  return PALETTE[hash(seed) % PALETTE.length]
}

/** Use within an on-screen member group to guarantee distinct adjacent colors. */
export function getAvatarColorClassByIndex(index: number): string {
  return PALETTE[Math.abs(index) % PALETTE.length]
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

export interface AvatarProps {
  name: string
  src?: string
  size?: keyof typeof SIZES
  /** `solid` = saturated fill, white initials. `soft` = tinted fill, tinted initials. */
  tone?: 'solid' | 'soft'
  className?: string
}

export function Avatar({
  name,
  src,
  size = 'md',
  tone = 'solid',
  className,
}: AvatarProps): React.ReactElement {
  const index = hash(name) % PALETTE.length
  const colorClass = tone === 'soft' ? SOFT_PALETTE[index] : PALETTE[index]
  if (src) {
    return (
      <img
        src={src}
        alt={name}
        className={cn('shrink-0 rounded-full object-cover', SIZES[size], className)}
      />
    )
  }
  return (
    <span
      aria-hidden
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-full font-semibold',
        tone === 'soft' ? '' : 'text-white',
        colorClass,
        SIZES[size],
        className
      )}
    >
      {initials(name)}
    </span>
  )
}

Avatar.displayName = 'Avatar'
